import { NoodleProductTypes } from '@typings/graphql-models';
import classNames from 'classnames';
import { ReactElement, useEffect, useState } from 'react';
import Button from '@components/DesignLibrary/Button';
import Modal from '@components/Modal';
import CustomerSchedulingCalendar from '@components/CustomerSchedulingCalendar';
import PencilSimple from '@components/Icons/PencilSimple';
import useNoodleApi from '@hooks/useNoodleApi';
import ProgressIndicator from '@components/ProgressIndicator';
import { Booking, Person } from '@typings/api-models';
import ProductIcon from '@components/ProductIcon';
import * as tsClient from '@tsClient';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import ConfirmationModal from '@modals/ConfirmationModal';
import s from './BookingCard.module.scss';
import Badge from '../DesignLibrary/Badge';
import CopyButton from '../CopyButton';

type Props = {
  booking: Pick<Booking, 'id' | 'productId' | 'startAt' | 'endAt' | 'priceId' | 'meetingLink' | 'userWorkflowId' | 'priceId'> & {
    person: Pick<Person, 'id' | 'name' | 'email' | 'phoneNumber' | 'contactPhoneNumber'>;
  };
  creatorTimezone: string;
  productType: NoodleProductTypes;
  productTitle?: string;
  priceTitle?: string;
  isRescheduleAvailable?: boolean;
  isCreator?: boolean;
  onFinish?: () => Promise<void>;
};

export const getBookingTypeText = (bookingType: NoodleProductTypes): string => {
  if (bookingType === NoodleProductTypes.InPersonSession) {
    return 'Live In-Person Session';
  }
  if (bookingType === NoodleProductTypes.OnlineSession) {
    return 'Live Online Session';
  }
  if (bookingType === NoodleProductTypes.Consultation) {
    return 'Consultation';
  }
  return 'Chat Session';
};

const BookingCard = ({
  booking,
  creatorTimezone,
  isRescheduleAvailable,
  isCreator,
  productType,
  productTitle,
  priceTitle,
  onFinish,
}: Props): ReactElement => {
  const { productId, startAt, endAt } = booking;
  const bookerName = booking.person.name || 'a client';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { getData: updateBooking } = useNoodleApi(tsClient.scheduling.updateBooking);

  const {
    data: productData,
    fetchingState: { isFetching: isFetchingProduct },
    getData: getProduct,
  } = useNoodleApi(tsClient.getProductById);

  useEffect(() => {
    getProduct({ id: productId });
  }, [productId, getProduct]);

  const getTimeText = (startString: string, endString: string): string => {
    const startDate = new Date(startString);
    const endDate = new Date(endString);
    return `${startDate.toLocaleTimeString('default', {
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      month: 'short',
      timeZone: creatorTimezone,
    })} – ${endDate.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit', timeZone: creatorTimezone })}`;
  };

  const handleBookingFinish = async (): Promise<void> => {
    setIsModalOpen(false);
    onFinish?.();
  };

  if (isFetchingProduct) {
    return <div className={s.bookingContainer}><ProgressIndicator isCentered /></div>;
  }

  const price = productData?.product.prices.find(p => p.id === booking.priceId)?.price;
  const isPaid = price ? price > 0 : false;

  return (
    <>
      <div className={s.bookingContainer}>
        {Boolean(startAt && endAt) && <p className={classNames('body-sm', s.timeText)}>{startAt && endAt && getTimeText(startAt, endAt)}</p>}
        <div>
          <p className={classNames('caption', s.productType)}>
            <ProductIcon noodleProductType={productType} size={12} />
            {productTitle || getBookingTypeText(productType)}
            {isCreator && <>{isPaid ? <Badge label="Paid" color="var(--color-success)" /> : <Badge label="Free" color="var(--color-gray-50)" />}</>}
          </p>
          {isCreator ? <p className="heading-sm">{bookerName}</p> : <p className="heading-sm">{priceTitle}</p>}
          {isCreator && (
            <>
              <div className={s.buttons}>
                {booking.meetingLink && (
                  <Button variant="primary" size="sm" href={booking.meetingLink}>
                    Join Meeting
                  </Button>
                )}
                {booking.userWorkflowId && <Button
                  variant="secondary"
                  size="sm"
                  href={getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: booking.userWorkflowId })}
                >
                  Open Workflow
                </Button>}
                <Button variant="secondary" size="sm" href={getUrl(IDENTIFIERS.DASHBOARD_MEMBER, { id: booking.person.id })}>
                  Open Client&apos;s Profile
                </Button>
              </div>
              {(booking.person.email || booking.person.phoneNumber) && (
                <ul className={s.contacts}>
                  {booking.person.email && (
                    <li>
                      <small className="caption">Email</small>
                      <CopyButton isWrapper className="body-sm" text={booking.person.email}>
                        {booking.person.email}
                      </CopyButton>
                    </li>
                  )}
                  {booking.person.phoneNumber && (
                    <li>
                      <small className="caption">Phone</small>
                      <CopyButton isWrapper className="body-sm" text={booking.person.phoneNumber}>
                        {booking.person.phoneNumber}
                      </CopyButton>
                    </li>
                  )}
                  {booking.person.contactPhoneNumber && (
                    <li>
                      <small className="caption">Contact Phone</small>
                      <CopyButton isWrapper className="body-sm" text={booking.person.contactPhoneNumber}>
                        {booking.person.contactPhoneNumber}
                      </CopyButton>
                    </li>
                  )}
                </ul>
              )}
            </>
          )}
          <div className={s.buttons}>
            <Button size="xs" onClick={() => setIsModalOpen(true)} variant="secondary" disabled={!isRescheduleAvailable}>
              <PencilSimple weight="fill" />
              {isRescheduleAvailable && productData?.product ? `${startAt ? 'Reschedule' : 'Schedule booking'}` : 'Reschedule not available'}
            </Button>
            {isRescheduleAvailable && isCreator && (
              <Button
                size="xs"
                onClick={async () => {
                  setIsConfirmationModalOpen(true);
                }}
                variant='destructive'
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          onClose={() => setIsConfirmationModalOpen(false)}
          text='Are you sure you want to cancel this booking?'
          onSubmit={async () => {
            await updateBooking({
              id: booking.id,
              updates: {
                canceledAt: new Date().toISOString(),
              },
            });
            setIsConfirmationModalOpen(false);
          }}
          cta='Cancel booking'
        />
      )}
      {isModalOpen && productData?.product && (
        <Modal

          title={`${startAt ? 'Reschedule' : 'Schedule'} ${isCreator ? bookerName : 'Booking'}`}
          onClose={() => setIsModalOpen(false)}

        >
          <div className={s.modal}>
            <CustomerSchedulingCalendar
              availabilityTimezone={creatorTimezone}
              priceId={booking.priceId}
              product={productData?.product}
              booking={booking}
              selectedDate={startAt}
              onFinish={handleBookingFinish}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default BookingCard;
